import fp from 'lodash/fp';
import React, { useCallback } from 'react';
import { Form, FormSpy, Field } from 'react-final-form';
import { checkSnils, checkOgrn, checkOgrnip } from 'ru-validation-codes';
import { rawRequest, contentTypeJson } from '@factorin/rest';
import { config } from '@factorin/container.macro';

import { validators, getError, wrapFormComponent } from 'Modules/core/forms';
import { wrapRequest } from 'Modules/core/api';
import { connect } from 'Modules/core/redux/utils';
import { Text, Icon, GridRow, GridColumn, Button, linkAnchorClass, Select, Input, Label, CheckBox } from 'Modules/ui2/components';
import info from 'Modules/ui2/icons/info.sprite.svg';
import warning from 'Modules/ui2/icons/warning.sprite.svg';

import success from './success.sprite.svg';
import commonStyles from '../common.module.scss';
import styles from './index.module.scss';


export const roles = [
    ['9312b1cb-3d0e-442d-9e4e-ac737eec6448', 'client'],
    ['e12411e0-6f75-4946-abed-56bc56cef9e0', 'factor'],
    ['562c5372-9f94-4f7a-b216-bd59a6a9b59f', 'debtor'],
];

const phoneMask = '+7(999)999-99-99';
const phoneValidation = getError(
    { key: 'phone' },
    v => v && v.length === phoneMask.length,
);

const isIPINN = inn => inn && inn.length === 12;

const ogrnValidation = getError({ key: 'ogrn' }, checkOgrn);
const ogrnipValidation = getError({ key: 'ogrnip' }, checkOgrnip);

const validationNone = getError({ key: 'none' }, v => fp.isEmpty(v));

const getFromInfo = fp.curry((title, info) => fp.pipe(
    fp.find(fp.pipe(
        fp.get('title'),
        fp.equals(title),
    )),
    fp.get('descr'),
    fp.trim,
)(info));

const getInnFromInfo = fp.pipe(
    info => getFromInfo('ИНН ЮЛ', info) || getFromInfo('ИНН', info),
    inn => inn.startsWith('00') ? inn.substring(2) : inn,
);

const isIPCert = fp.pipe(getInnFromInfo, isIPINN);

const isIndividual = info => !getFromInfo('ОГРНИП', info) && !getFromInfo('ОГРН', info);

const mapCertInfo = {
    surname: info => getFromInfo('Фамилия', info) || getFromInfo('Владелец', info),
    firstName: fp.pipe(
        getFromInfo('Имя Отчество'),
        fp.split(' '),
        fp.get(0),
    ),
    middleName: fp.pipe(
        getFromInfo('Имя Отчество'),
        fp.split(' '),
        fp.drop(1),
        fp.join(' '),
    ),
    role: () => '9312b1cb-3d0e-442d-9e4e-ac737eec6448',
    email: getFromInfo('Email'),
    company: info => !isIPCert(info) ? getFromInfo('Компания', info) : null,
    kpp: getFromInfo('KPP'),
    ogrn: info => getFromInfo(isIPCert(info) ? 'ОГРНИП' : 'ОГРН', info),
    inn: info => !isIndividual(info) ? getInnFromInfo(info) : null,
    snils: fp.pipe(
        getFromInfo('СНИЛС'),
        snils => {
            if (!snils)
                return '';

            if (checkSnils(snils))
                return snils;

            const formattedSnils = `${
                snils.substring(0, 3)}-${
                snils.substring(3, 6)}-${
                snils.substring(6, 9)} ${
                snils.substring(9, 12)}`;

            if (checkSnils(formattedSnils))
                return formattedSnils;

            return '';
        },
    ),
};

const formMutators = {
    setCertificate: ([{ info }], state, { changeValue }) => fp.pipe(
        fp.mapValues(getter => getter(info)),
        fp.toPairs,
        fp.forEach(([key, value]) => changeValue(state, key, () => value)),
    )(mapCertInfo),
};

const findSavedCertificate = (savedTumbprint, certificates) => fp.find(
    ({ thumbprint }) => thumbprint === savedTumbprint,
    certificates,
);

const saveFormData = ({ values: { certificate, ...rest } }) =>
    window.localStorage.setItem('registrationForm', JSON.stringify({
        thumbprint: certificate ? certificate.thumbprint : null,
        ...rest,
    }));

export const resetFormData = () => window.localStorage.removeItem('registrationForm');

const initialValues = (certificates, roleOverride) => {
    try {
        const savedData = window.localStorage.getItem('registrationForm');
        if (savedData) {
            const { thumbprint, role, name, ...parsedData } = JSON.parse(savedData);
            if (thumbprint) {
                const certificate = findSavedCertificate(thumbprint, certificates);
                if (certificate) {
                    const nameParts = fp.split(' ', name);
                    return {
                        certificate,
                        role: `${role}`.length === 1 ? '9312b1cb-3d0e-442d-9e4e-ac737eec6448' : role,
                        ...name && {
                            surname: nameParts[0],
                            firstName: nameParts[1],
                            middleName: fp.pipe(
                                fp.drop(2),
                                fp.join(' '),
                            )(nameParts),
                        },
                        ...parsedData,
                        ...roleOverride && { role: roleOverride },
                    };
                }
            }
        }
    } catch {}

    const [certificate] = certificates;
    return {
        certificate,
        role: roleOverride,
        ...fp.mapValues(getter => getter(certificate.info), mapCertInfo),
    };
};

const formDataMap = {
    Surname: fp.get('surname'), // Ф
    FirstName: fp.get('firstName'), // И
    MiddleName: fp.get('middleName'), // О
    Email: fp.get('email'),
    PhoneNumber: fp.get('phone'),
    CompanyName: fp.get('company'),
    CompanyINN: fp.get('inn'),
    CompanyKPP: fp.get('kpp'),
    SNILS: fp.get('snils'),
    CompanyOGRN: fp.get('ogrn'),
    AllowUsePersonalData: fp.get('usePersonalData'),
    Base64Certificate: fp.get('certificate.base64'),
    UserRoleUid: fp.get('role'),
};

const CertificateField = wrapFormComponent(({ onChange, mutate, ...props }) => (
    <Select
        ellipsis={false}
        onChange={useCallback(
            cert => {
                mutate(cert);
                onChange(cert);
            },
            [mutate, onChange],
        )}
        {...props}
    />
));

const SelectField = wrapFormComponent(Select);
const InputField = wrapFormComponent(Input);
export const CheckBoxField = wrapFormComponent(CheckBox);

export class RegistrationForm extends React.PureComponent {
    state = {
        busy: false,
    };

    saveFormData = fp.debounce(100, form =>
        !this.state.busy && saveFormData(form));

    static getDerivedStateFromProps = ({ certificates, fixedRole }) => ({
        certificates: certificates.map(cert => ({
            label: cert.label,
            value: cert,
        })),
        initialValues: initialValues(certificates, fixedRole),
    });

    submitForm = async values => {
        if (this.state.busy)
            return;

        const { t, setStage, clientOfficeUri = '', landingOwnerUid } = this.props;

        saveFormData({ values });
        await new Promise(resolve => this.setState({ busy: true }, resolve));

        const apiValues = fp.mapValues(getter => getter(values), formDataMap);
        const requestUrl = fp.pipe(fp.compact, fp.join(''))([
            clientOfficeUri || config.basePath,
            '/api/v1',
            '/registration/create',
        ]);

        try {
            const data = await wrapRequest(rawRequest, {
                method: 'POST',
                url: requestUrl,
                headers: {
                    Accept: contentTypeJson,
                    'Accept-Language': 'ru',
                    'Content-Type': contentTypeJson,
                },
                data: JSON.stringify({
                    ...apiValues,
                    LandingOwnerUid: landingOwnerUid,
                }),
            });

            if (data.Created) {
                resetFormData();
                setStage('success', { email: values.email });
            } else {
                setStage('resendLetter', {
                    email: values.email,
                    timer: data.ConfirmCodeRequestRemaining,
                });
            }
        } catch (error) {
            setStage('failure', { error: error.message || t('failure.unknown') });
        }
    };

    render() {
        const { t, fixedRole } = this.props;
        const { busy, certificates, initialValues } = this.state;
        return (
            <Form
                initialValues={initialValues}
                mutators={formMutators}
                subscription={{ hasValidationErrors: true, values: true }}
                onSubmit={this.submitForm}
            >
                {({
                    form: { mutators: { setCertificate } },
                    values,
                    hasValidationErrors,
                    handleSubmit,
                }) => (
                    <form onSubmit={handleSubmit}>
                        <FormSpy onChange={this.saveFormData} />
                        <Text className={styles.note} component='div' textSize='18'>
                            Формы заполняются автоматически на основе данных УКЭП
                            <br />
                            <Icon className={commonStyles.infoIcon} icon={info} />&nbsp;
                            Проверьте и заполните недостающие поля
                        </Text>
                        <GridRow className={styles.row}>
                            <GridColumn size={5}>
                                <Field
                                    name='certificate'
                                    component={CertificateField}
                                    size='56'
                                    validate={validators.required}
                                    label={t('entry.fields.certificate.label')}
                                    className={styles.select}

                                    mutate={setCertificate}

                                    disabled={busy}
                                    options={certificates}
                                    placeholder={t('entry.fields.certificate.placeholder')}
                                />
                            </GridColumn>
                            <GridColumn offset={1} size={3}>
                                <Field
                                    name='phone'
                                    component={InputField}
                                    size='56'
                                    validate={validators.pipe(
                                        validators.required,
                                        phoneValidation,
                                    )}
                                    needInitialValidate
                                    label={t('entry.fields.phone.label')}

                                    disabled={busy}
                                    mask={phoneMask}
                                    placeholder={phoneMask}
                                />
                            </GridColumn>
                        </GridRow>
                        <GridRow className={styles.row}>
                            <GridColumn size={4}>
                                <Field
                                    name='role'
                                    component={SelectField}
                                    size='56'
                                    validate={validators.required}
                                    needInitialValidate
                                    label={t('entry.fields.role.label')}
                                    className={styles.select}

                                    disabled={busy || fixedRole}
                                    options={roles.map(([id, name]) => ({
                                        value: id,
                                        label: t(`entry.roles.${name}`),
                                    }))}
                                    placeholder={t('entry.fields.role.placeholder')}
                                />
                            </GridColumn>
                            <GridColumn offset={2} size={3}>
                                <Field
                                    name='inn'
                                    component={InputField}
                                    size='56'
                                    validate={validators.pipe(
                                        validators.required,
                                        validators.is.inn,
                                    )}
                                    needInitialValidate
                                    label={t(`entry.fields.inn.label.${
                                        isIPINN(values.inn)
                                            ? 'individual'
                                            : 'company'
                                    }`)}

                                    disabled={busy}
                                    placeholder='1234567890'
                                />
                            </GridColumn>
                        </GridRow>
                        <GridRow className={styles.row}>
                            <GridColumn size={5}>
                                <Field
                                    name='surname'
                                    component={InputField}
                                    size='56'
                                    validate={validators.required}
                                    needInitialValidate
                                    label={t('entry.fields.surname.label')}

                                    disabled={busy}
                                    placeholder={t('entry.fields.surname.placeholder')}
                                />
                            </GridColumn>
                            <GridColumn offset={1} size={5}>
                                <Field
                                    name='company'
                                    component={InputField}
                                    size='56'
                                    validate={(company, values) =>
                                        isIPINN(values.inn)
                                            ? validationNone(company)
                                            : validators.required(company)
                                    }
                                    needInitialValidate
                                    label={t('entry.fields.company.label')}

                                    disabled={busy}
                                    placeholder={isIPINN(values.inn)
                                        ? t('entry.fields.notApplicable')
                                        : t('entry.fields.company.placeholder')}
                                />
                            </GridColumn>
                        </GridRow>
                        <GridRow className={styles.row}>
                            <GridColumn size={5}>
                                <Field
                                    name='firstName'
                                    component={InputField}
                                    size='56'
                                    validate={validators.required}
                                    needInitialValidate
                                    label={t('entry.fields.firstName.label')}

                                    disabled={busy}
                                    placeholder={t('entry.fields.firstName.placeholder')}
                                />
                            </GridColumn>
                            <GridColumn offset={1} size={3}>
                                <Field
                                    name='ogrn'
                                    component={InputField}
                                    size='56'
                                    validate={(ogrn, values) => validators.pipe(
                                        validators.required,
                                        isIPINN(values.inn)
                                            ? ogrnipValidation
                                            : ogrnValidation,
                                    )(ogrn)}
                                    needInitialValidate
                                    label={t(`entry.fields.ogrn.label.${
                                        isIPINN(values.inn)
                                            ? 'individual'
                                            : 'company'
                                    }`)}

                                    disabled={busy}
                                    placeholder='1234567890123'
                                />
                            </GridColumn>
                        </GridRow>
                        <GridRow className={styles.row}>
                            <GridColumn size={5}>
                                <Field
                                    name='middleName'
                                    component={InputField}
                                    size='56'
                                    label={t('entry.fields.middleName.label')}

                                    disabled={busy}
                                    placeholder={t('entry.fields.middleName.placeholder')}
                                />
                            </GridColumn>
                            <GridColumn offset={1} size={3}>
                                <Field
                                    name='kpp'
                                    component={InputField}
                                    size='56'
                                    validate={(kpp, values) =>
                                        isIPINN(values.inn)
                                            ? validationNone(kpp)
                                            : validators.pipe(
                                                validators.required,
                                                validators.is.kpp,
                                            )(kpp)
                                    }
                                    needInitialValidate
                                    label={t('entry.fields.kpp.label')}

                                    disabled={busy}
                                    placeholder={isIPINN(values.inn)
                                        ? t('entry.fields.notApplicable')
                                        : '123456789'}
                                />
                            </GridColumn>
                        </GridRow>
                        <GridRow className={styles.row}>
                            <GridColumn size={4}>
                                <Field
                                    name='email'
                                    component={InputField}
                                    size='56'
                                    validate={validators.pipe(
                                        validators.required,
                                        validators.is.email,
                                    )}
                                    needInitialValidate
                                    label={t('entry.fields.email.label')}

                                    disabled={busy}
                                    placeholder='inbox@company.org'
                                />
                            </GridColumn>
                            <GridColumn offset={2} size={3}>
                                <Field
                                    name='snils'
                                    component={InputField}
                                    size='56'
                                    validate={validators.pipe(
                                        validators.required,
                                        validators.is.snils,
                                    )}
                                    needInitialValidate
                                    label={t('entry.fields.snils.label')}

                                    disabled={busy}
                                    mask='999-999-999 99'
                                    placeholder='123-456-789 01'
                                />
                            </GridColumn>
                        </GridRow>
                        <GridRow className={styles.row}>
                            <GridColumn size={6}>
                                <Field
                                    name='usePersonalData'
                                    component={CheckBoxField}
                                    validate={validators.required}
                                    className={styles.checkboxField}
                                    disabled={busy}
                                    type='checkbox'
                                >
                                    <Label
                                        component='span'
                                        textSize='14'
                                        style={{ marginTop: 3, display: 'inline-block' }}
                                    >
                                        {t('entry.legal', { linkClass: linkAnchorClass })}
                                    </Label>
                                </Field>
                                <FormSpy subscription={{ errors: true }}>
                                    {({ errors }) => {
                                        const personalError = fp.get('usePersonalData', errors);
                                        return personalError
                                            ? (
                                                <div className={styles.errorsContainer}>
                                                    <span className={styles.errorsLabel}>
                                                        {t('formField:usePersonalData')}
                                                    </span>
                                                </div>
                                            )
                                            : null;
                                    }}
                                </FormSpy>
                            </GridColumn>
                        </GridRow>
                        <Button
                            variant='normal'
                            size='56'
                            type='submit'
                            disabled={hasValidationErrors || busy}
                            loading={busy}
                        >
                            {t('entry.submit')}
                        </Button>
                    </form>
                )}
            </Form>
        );
    }
}

export const FormComponent = connect(
    ({ landing }) => ({
        clientOfficeUri: fp.get('config.clientofficeUrl', landing),
    }),
)(RegistrationForm);

export const SuccessfulRegistrationComponent = ({ t, email }) => (
    <>
        <Icon icon={success} className={styles.successIcon} size='56' />
        <Label component='h3' weight='demibold' textSize='21-24'>
            {t('success.title')}
        </Label>
        <Text component='p' className={commonStyles.description} textSize='18'>
            {t('success.description', { email, linkClass: linkAnchorClass })}
        </Text>
    </>
);

export const FailedRegistrationComponent = ({ t, error, setStage }) => (
    <>
        <Icon icon={warning} className={commonStyles.errorIcon} size='48' />
        <Label component='h3' weight='demibold' textSize='21-24'>
            {t('failure.title')}
        </Label>
        <Text component='p' className={commonStyles.description} textSize='18'>
            {error}
        </Text>
        <Text component='p' className={commonStyles.description} textSize='18'>
            {t('failure.description')}
        </Text>
        <Button
            variant='normal'
            className={commonStyles.button}
            size='56'
            type='button'
            onClick={() => setStage('formEntry')}
        >
            {t('failure.backToForm')}
        </Button>
    </>
);
